import { AgGridSolidRef } from '@ag-grid-community/solid';
import {
  Box,
  Checkbox,
  IconButton,
  Stack,
  Chip,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import { BasicModal, Button } from '@components';
import { SiteTitle } from '@components/SiteTitle/SiteTitle';
import {
  getATGDormantPoolDataAction,
  getClaimedCustomersAction,
  getDormantPoolGridAction,
  getPoolSizeAction,
} from '@store/dormantPool';
import { dormatPoolStore, setDormantPoolStore } from '@store/dormantPool/store';
import { closeModal, openModal } from '@store/modals';
import { AppsIcon, ViewListIcon } from '@atgfr8/icons';
import { DormantPoolGridQueryParamsType } from '@typeDefinitions/dormantPoolTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { createSignal, onMount, Show } from 'solid-js';
import { produce } from 'solid-js/store';

import { GridView } from './components/GridView';
import { GridViewFilters } from './components/GridViewFilters';
import { ListView } from './components/ListView';

const DormantPool = () => {
  const [viewMode, setViewMode] = createSignal<'List' | 'Grid'>('List');

  const [gridRef, setGridRef] = createSignal<AgGridSolidRef | undefined>();
  const [gridPayload, setGridPayload] = createSignal<Record<string, unknown>>(
    {},
  );
  const [isGridLoading, setIsGridLoading] = createSignal(true);

  const [areFiltersActive, setAreFiltersActive] = createSignal(false);
  const [hideNotInterested, setHideNotInterested] = createSignal(false);

  onMount(async () => {
    await getPoolSizeAction();
    await getClaimedCustomersAction();
    await getATGDormantPoolDataAction();
  });

  const handleHideNotInterested = async (checked: boolean) => {
    setHideNotInterested(checked);
    if (viewMode() === 'List') {
      gridRef()?.api.refreshServerSide({ purge: true });
    } else {
      await handleApplyFilters(checked);
    }
  };

  const handleApplyFilters = async (overrideHide?: boolean) => {
    const hide = overrideHide ?? hideNotInterested();
    const {
      originStates = [],
      destinationStates = [],
      equipment = [],
    } = dormatPoolStore.gridView;

    const hasFilters = !!(
      originStates.length ||
      destinationStates.length ||
      equipment.length
    );

    const payload: DormantPoolGridQueryParamsType = {
      hideNotInterested: hide,
      ...(hasFilters && {
        ...(originStates.length && { originStates }),
        ...(destinationStates.length && { destinationStates }),
        ...(equipment.length && { equipment }),
      }),
    };

    setIsGridLoading(true);
    await getDormantPoolGridAction(payload);
    setAreFiltersActive(hasFilters);
    setIsGridLoading(false);
  };

  const clearFilters = () => {
    setDormantPoolStore(
      produce((draft) => {
        draft.gridView.originStates = [];
        draft.gridView.destinationStates = [];
        draft.gridView.equipment = [];
      }),
    );
    setAreFiltersActive(false);
    setViewMode('List');
  };

  return (
    <>
      <SiteTitle title={'Dormant Customers'} />
      <Stack
        spacing={2}
        class="rounded-md! h-full overflow-hidden"
        backgroundColor="#E1ECF2"
      >
        <Stack
          spacing={0.1}
          backgroundColor="white"
          class="sticky top-0 z-40 bg-white shadow-lg! px-4 py-2 md:h-fit! sm:flex-col xl:flex-row"
          justifyContent="space-between"
        >
          <Box class="flex flex-wrap" alignItems="center" gap={2}>
            <Typography variant="h5" class="truncate pl-4!">
              Dormant Customers
            </Typography>

            <Stack direction="row" alignItems="center" spacing={1}>
              <IconButton
                aria-label="List View"
                title="List View"
                style={{
                  color:
                    viewMode() === 'List' ? '#1B4960' : 'rgba(0, 0, 0, 0.54)',
                }}
                onClick={() => {
                  setViewMode('List');
                }}
                dataTestId="dormant-pool-list-view-button"
              >
                <ViewListIcon />
              </IconButton>

              <IconButton
                aria-label="Grid View"
                title="Grid View"
                style={{
                  color:
                    viewMode() === 'Grid' ? '#1B4960' : 'rgba(0, 0, 0, 0.54)',
                }}
                onClick={() => {
                  setViewMode('Grid');
                }}
                dataTestId="dormant-pool-grid-view-button"
              >
                <AppsIcon />
              </IconButton>

              <Show when={viewMode() === 'Grid'}>
                <Button
                  label="Apply Filters"
                  onClick={() => openModal('grid_filters')}
                  size="sm"
                  dataTestId={formatDataTestId('Apply Filters', 'button')}
                />
              </Show>

              <Show when={viewMode() === 'Grid' && areFiltersActive()}>
                <Button
                  label="Clear Filters"
                  onClick={clearFilters}
                  color="error"
                  size="sm"
                  dataTestId={formatDataTestId('Clear Filters', 'button')}
                />
              </Show>

              <Checkbox
                label="Hide Customers Marked as Not Interested"
                checked={hideNotInterested()}
                onChange={(checked) => {
                  void handleHideNotInterested(checked);
                }}
                style={{ 'vertical-align': 'text-bottom' }}
                dataTestId="dormant-pool-hide-not-interested-checkbox"
              />
            </Stack>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              id="pool-size-chip"
              text={String(dormatPoolStore.poolSize || 0)}
            />
            <Typography variant="caption" class="whitespace-nowrap">
              Available Customers
            </Typography>
          </Box>
        </Stack>

        <Box class="p-3 m-0! flex-1 overflow-auto">
          <Show when={viewMode() === 'List'}>
            <ListView
              gridRef={gridRef}
              setGridRef={setGridRef}
              hideNotInterested={hideNotInterested}
              viewMode={viewMode}
              gridPayload={gridPayload}
              setGridPayload={setGridPayload}
            />
          </Show>
          <Show when={viewMode() === 'Grid'}>
            <GridView
              hideNotInterested={hideNotInterested}
              viewMode={viewMode}
              isGridLoading={isGridLoading}
              setIsGridLoading={setIsGridLoading}
              onCustomerRemovedFromGrid={handleApplyFilters}
            />
          </Show>
        </Box>
      </Stack>

      <BasicModal
        title="Dormant Account Filters"
        id="grid_filters"
        width="70vw"
        onSubmitText="Apply Filters"
        onSubmit={() => handleApplyFilters()}
        onCloseText="Close"
        onClose={() => {
          closeModal('grid_filters');
        }}
        class="max-h-[100vh]! xl:max-h-auto!"
        footer
      >
        <GridViewFilters />
      </BasicModal>
    </>
  );
};

export default DormantPool;
